import React from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Shield, TrendingUp, Target, Users2, Compass } from 'lucide-react';
export function CivilSocietyPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-150">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-151">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-152">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-153">
            <span className="w-8 h-px bg-teal" data-id="element-154"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-155">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-156"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-157">
            Civil Society and Community Capability
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-158">
            SyNeural's platform architecture is designed to help civil society
            organizations, nonprofits, mentorship networks, and community
            institutions strengthen the human capabilities that support
            healthier communities and more resilient civic life.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-159">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-160">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-161">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying developmental bottlenecks,
            and connecting those bottlenecks to practical pathways for growth,
            support, and opportunity.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-162">
            Civil society plays a vital role in shaping how people develop
            outside formal government and market systems. It is often where
            mentorship happens, where support networks are built, where
            belonging is reinforced, and where people either gain or lose access
            to the pathways that help them grow. SyNeural helps make those
            dynamics more visible and more actionable, supporting stronger
            communities from the ground up.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-163">
        {/* Community Capability */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-164">
          <div className="flex items-center gap-4 mb-6" data-id="element-165">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-166">
              <Users size={28} data-id="element-167" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-168">
              Community Capability and Local Resilience
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-169">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-170">
              SyNeural can help civil society organizations better understand
              the capabilities that shape community resilience. These include
              reasoning quality, self-direction, emotional regulation, social
              cooperation, manipulation resistance, and opportunity access. By
              making these patterns more visible, the platform can support
              stronger local interventions, better mentoring structures, and
              more coherent pathways for individual and community development.
            </p>
          </div>
        </motion.div>

        {/* Misinformation Resistance */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-171">
          <div className="flex items-center gap-4 mb-6" data-id="element-172">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-173">
              <Shield size={28} data-id="element-174" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-175">
              Misinformation Resistance and Civic Stability
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-176">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-177">
              Many communities are increasingly vulnerable to rumor, emotional
              contagion, manipulation, and fragmented information environments.
              SyNeural can help civil society organizations strengthen media
              literacy, independent judgment, and resilience against propaganda
              and rhetorical capture. This makes the platform especially
              relevant for organizations working on civic trust, democratic
              resilience, social stability, and healthy public discourse.
            </p>
          </div>
        </motion.div>

        {/* Mentorship and Upward Mobility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-178">
          <div className="flex items-center gap-4 mb-6" data-id="element-179">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-180">
              <TrendingUp size={28} data-id="element-181" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-182">
              Mentorship and Upward Mobility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-183">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-184">
              One of the most practical uses of the platform in civil society
              settings is connecting underdeveloped potential to real pathways
              for progress. SyNeural can help identify where individuals may
              need stronger support in confidence, planning, self-direction,
              communication, or opportunity access, then align those needs with
              mentorship, training, peer reinforcement, and community-based
              support systems. This makes it useful for upward mobility and
              human development initiatives.
            </p>
          </div>
        </motion.div>

        {/* Youth Development */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-185">
          <div className="flex items-center gap-4 mb-6" data-id="element-186">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-187">
              <Heart size={28} data-id="element-188" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-189">
              Youth Development and Community Leadership
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-190">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-191">
              Civil society organizations often play a major role in helping
              young people develop the habits, confidence, and support
              structures that shape later outcomes. SyNeural can help strengthen
              those efforts by supporting emotional regulation, self-direction,
              leadership formation, and reasoning development in youth-centered
              programs. It can also support the cultivation of future community
              leaders who are better equipped to handle responsibility and
              contribute constructively.
            </p>
          </div>
        </motion.div>

        {/* Support Systems */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-192">
          <div className="flex items-center gap-4 mb-6" data-id="element-193">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-194">
              <Target size={28} data-id="element-195" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-196">
              Support Systems and Opportunity Alignment
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-197">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-198">
              Communities are often strongest when support systems are visible,
              connected, and aligned with real developmental needs. SyNeural
              helps make this alignment more structured by showing where people
              need stronger pathways, where support is missing, and where local
              institutions can play a more effective role. This can help
              nonprofits, mentorship networks, and local initiatives build more
              coherent systems of encouragement, development, and advancement.
            </p>
          </div>
        </motion.div>

        {/* Local Leadership */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-199">
          <div className="flex items-center gap-4 mb-6" data-id="element-200">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-201">
              <Compass size={28} data-id="element-202" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-203">
              Local Leadership and Civic Participation
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-204">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-205">
              Civil society depends on people who can think clearly, cooperate
              well, resist manipulation, and contribute with responsibility.
              SyNeural can help identify and strengthen these capabilities in
              individuals moving into community leadership, organizing,
              mentorship, or civic participation roles. This makes it relevant
              for strengthening the broader human foundation on which healthier
              local institutions and public life depend.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-206">
        <div className="max-w-4xl mx-auto" data-id="element-207">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-208">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-209">
              Why It Matters for Civil Society
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-210">
              Civil society is where much of the real work of human development,
              belonging, and support takes place. SyNeural helps organizations
              working in that space move from intuition alone toward a more
              structured understanding of capability, readiness, and
              opportunity. It offers a framework for helping communities develop
              stronger people, stronger support systems, and stronger local
              resilience over time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}